import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ApiService from "services/ApiService";
import { signOut } from "./authSlice";

export const ANNOUNCEMENT_SEEN_STORAGE_KEY = "seenAnnouncementIds";

const getStoredSeenAnnouncementIds = () => {
  if (typeof window === "undefined") return [];
  try {
    const storedIds = JSON.parse(
      window.localStorage.getItem(ANNOUNCEMENT_SEEN_STORAGE_KEY) || "[]",
    );
    return Array.isArray(storedIds) ? storedIds : [];
  } catch (error) {
    return [];
  }
};

export const initialState = {
  items: [],
  seenAnnouncementIds: getStoredSeenAnnouncementIds(),
  loading: false,
  saving: false,
  error: null,
};

const getErrorPayload = (error, fallbackMessage) =>
  error.response?.data || error.message || fallbackMessage;

export const fetchAnnouncements = createAsyncThunk(
  "announcements/fetchAnnouncements",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiService.getAnnouncements();
      if (response.status === 200 && response.data?.success) {
        return response.data.data || [];
      }
      return rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Unable to retrieve announcements."),
      );
    }
  },
);

export const createAnnouncement = createAsyncThunk(
  "announcements/createAnnouncement",
  async (announcement, { rejectWithValue }) => {
    try {
      const response = await ApiService.createAnnouncement(announcement);
      if (response.status === 200 && response.data?.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Unable to create announcement."),
      );
    }
  },
);

export const updateAnnouncement = createAsyncThunk(
  "announcements/updateAnnouncement",
  async (announcement, { rejectWithValue }) => {
    try {
      const response = await ApiService.updateAnnouncement(announcement);
      if (response.status === 200 && response.data?.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Unable to update announcement."),
      );
    }
  },
);

export const deleteAnnouncement = createAsyncThunk(
  "announcements/deleteAnnouncement",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiService.deleteAnnouncement(id);
      if (response.status === 200 && response.data?.success) {
        return response.data.data?.id || id;
      }
      return rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Unable to delete announcement."),
      );
    }
  },
);

export const announcementSlice = createSlice({
  name: "announcements",
  initialState,
  reducers: {
    markAnnouncementSeen: (state, action) => {
      if (!state.seenAnnouncementIds.includes(action.payload)) {
        state.seenAnnouncementIds.push(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnnouncements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchAnnouncements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAnnouncement.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createAnnouncement.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.saving = false;
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateAnnouncement.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        const index = state.items.findIndex(
          (announcement) => announcement.id === action.payload.id,
        );
        if (index !== -1) state.items[index] = action.payload;
        state.saving = false;
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.items = state.items.filter(
          (announcement) => announcement.id !== action.payload,
        );
        state.loading = false;
      })
      .addCase(deleteAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(signOut.fulfilled, (state) => ({
        ...initialState,
        seenAnnouncementIds: state.seenAnnouncementIds,
      }));
  },
});

export const { markAnnouncementSeen } = announcementSlice.actions;
export default announcementSlice.reducer;
